import React, { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { safeStringify } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { doc, onSnapshot, collection, getDocs, query, orderBy, limit, setDoc, serverTimestamp, deleteDoc, addDoc, Timestamp, writeBatch } from 'firebase/firestore';
import { Activity, Clock, AlertTriangle, CheckCircle, BarChart3, Users, Building, BookOpen, GraduationCap, MessageSquare, Terminal, RefreshCw, Mail, ShieldAlert, Monitor, ArrowRight, User, X } from 'lucide-react';
import {
  DEFAULT_ORGANISATION_PLAN_LIMITS,
  DEFAULT_USER_PLAN_LIMITS,
  formatPlanStatus,
  PLAN_STATUSES,
  PlanLimits,
  PlanStatus,
  resolvePlanLimits,
  resolvePlanStatus
} from '../config/plans';

export default function AdminDashboard() {
  const { user: currentUser } = useAuth();
  const isPlatformOwner = currentUser?.email === 'info@luminalearn.pro' || currentUser?.email === 'victord70@gmail.com';
  
  const [activeTab, setActiveTab] = useState<'system' | 'jobs' | 'users' | 'orgs' | 'analytics' | 'feedback' | 'errors'>('system');
  const [metrics, setMetrics] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Platform Data
  const [usersList, setUsersList] = useState<any[]>([]);
  const [orgsList, setOrgsList] = useState<any[]>([]);
  const [coursesList, setCoursesList] = useState<any[]>([]);
  const [enrollmentsList, setEnrollmentsList] = useState<any[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // User Management
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<any | null>(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [actionFeedback, setActionFeedback] = useState<{message: string, type: 'success' | 'error'} | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const showFeedback = (message: string, type: 'success' | 'error' = 'success') => {
    setActionFeedback({ message, type });
    setTimeout(() => setActionFeedback(null), 5000);
  };

  // Pilot MVP data streams
  const [feedbackList, setFeedbackList] = useState<any[]>([]);
  const [errorsList, setErrorsList] = useState<any[]>([]);
  const [jobsList, setJobsList] = useState<any[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [errorsLoading, setErrorsLoading] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null);

  // Usage visibility
  const [usageEventsList, setUsageEventsList] = useState<any[]>([]);
  const [usageDailyList, setUsageDailyList] = useState<any[]>([]);
  const [organisationUsageDailyList, setOrganisationUsageDailyList] = useState<any[]>([]);
  const [usageLoading, setUsageLoading] = useState(false);

  useEffect(() => {
    // Subscribe to system metrics
    const unsub = onSnapshot(doc(db, 'system', 'metrics'), (snapshot) => {
      if (snapshot.exists()) {
        setMetrics(snapshot.data());
        setError(null);
      } else {
        setError("Metrics document does not exist yet. Please wait for the system to initialize.");
      }
    }, (err: any) => {
      console.error("Firestore Error:", err?.message || err);
      setError(`Permission denied or connection error: ${err.message}`);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (activeTab === 'users' || activeTab === 'orgs' || activeTab === 'analytics') {
      fetchAllPlatformData();
    }

    if (activeTab === 'analytics') {
      setUsageLoading(true);

      const usageEventsQuery = query(collection(db, 'usageEvents'), orderBy('createdAt', 'desc'), limit(150));
      const usageDailyQuery = query(collection(db, 'usageDaily'), orderBy('updatedAt', 'desc'), limit(100));
      const organisationUsageDailyQuery = query(collection(db, 'organisationUsageDaily'), orderBy('updatedAt', 'desc'), limit(100));

      const unsubUsageEvents = onSnapshot(usageEventsQuery, (snapshot) => {
        setUsageEventsList(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        setUsageLoading(false);
      }, (err: any) => {
        console.error("Firestore Usage Events error:", err?.message || err);
        setUsageLoading(false);
      });

      const unsubUsageDaily = onSnapshot(usageDailyQuery, (snapshot) => {
        setUsageDailyList(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      }, (err: any) => {
        console.error("Firestore Usage Daily error:", err?.message || err);
      });

      const unsubOrganisationUsageDaily = onSnapshot(organisationUsageDailyQuery, (snapshot) => {
        setOrganisationUsageDailyList(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      }, (err: any) => {
        console.error("Firestore Organisation Usage Daily error:", err?.message || err);
      });

      return () => {
        unsubUsageEvents();
        unsubUsageDaily();
        unsubOrganisationUsageDaily();
      };
    }

    if (activeTab === 'jobs') {
      setJobsLoading(true);
      const q = query(collection(db, 'jobs'), orderBy('createdAt', 'desc'), limit(150));
      const unsub = onSnapshot(q, (snapshot) => {
        setJobsList(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        setJobsLoading(false);
      }, (err: any) => {
        console.error("Firestore Error:", err?.message || err);
        setJobsLoading(false);
      });
      return () => unsub();
    }

    if (activeTab === 'feedback') {
      setFeedbackLoading(true);
      const q = query(collection(db, 'pilot_feedback'), orderBy('createdAt', 'desc'), limit(150));
      const unsub = onSnapshot(q, (snapshot) => {
        setFeedbackList(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        setFeedbackLoading(false);
      }, (err: any) => {
        console.error("Firestore Feedback error:", err?.message || err);
        setFeedbackLoading(false);
      });
      return () => unsub();
    }

    if (activeTab === 'errors') {
      setErrorsLoading(true);
      const q = query(collection(db, 'app_errors'), orderBy('timestamp', 'desc'), limit(150));
      const unsub = onSnapshot(q, (snapshot) => {
        setErrorsList(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        setErrorsLoading(false);
      }, (err: any) => {
        console.error("Firestore Error Log error:", err?.message || err);
        setErrorsLoading(false);
      });
      return () => unsub();
    }
  }, [activeTab]);

  const fetchAllPlatformData = async () => {
    setIsLoadingData(true);
    try {
      const [usersSnap, orgsSnap, coursesSnap, enrollmentsSnap] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'organisations')),
        getDocs(collection(db, 'courses')),
        getDocs(collection(db, 'enrollments'))
      ]);

      setUsersList(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setOrgsList(orgsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCoursesList(coursesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setEnrollmentsList(enrollmentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err: any) {
      console.error("Error fetching platform data:", err?.message || err);
    } finally {
      setIsLoadingData(false);
    }
  };

  const formatTime = (ms: number) => {
    if (ms > 1000) return `${(ms / 1000).toFixed(2)}s`;
    return `${Math.round(ms)}ms`;
  };

  const formatTimestamp = (ts: any) => {
    if (!ts) return 'just now';
    if (ts.toDate) return ts.toDate().toLocaleString();
    return new Date(ts).toLocaleString();
  };

  const [expandedJobIds, setExpandedJobIds] = useState<string[]>([]);

  const canRetryJob = (job: any) => {
    const terminalStatuses = ['failed', 'timed_out', 'cancelled'];
    const attemptCount = Number(job?.attemptCount || 0);
    const maxAttempts = Number(job?.maxAttempts || 3);
    return terminalStatuses.includes(job?.status) && attemptCount < maxAttempts;
  };

  const toggleExpandedJob = (jobId: string) => {
    setExpandedJobIds(prev => prev.includes(jobId) ? prev.filter(id => id !== jobId) : [...prev, jobId]);
  };

  const handleRetryJob = async (job: any) => {
    if (!currentUser || !job?.id) return;
    try {
      setRetryingJobId(job.id);
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/jobs/${job.id}/retry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(currentUser?.uid ? { 'x-organization-id': job.organisationId || '' } : {})
        }
      });
      const result = await response.json();
      if (!response.ok || !result?.success) {
        throw new Error(result?.error || 'Retry request failed');
      }
      showFeedback(`Retry requested for job ${job.id.slice(0, 8)}...`);
    } catch (err: any) {
      console.error('Retry job failed', err);
      showFeedback(err?.message || 'Retry failed', 'error');
    } finally {
      setRetryingJobId(null);
    }
  };

  const togglePlatformAdmin = async (targetUserId: string, currentStatus: boolean, targetEmail: string) => {
    if (!isPlatformOwner || !currentUser) return;
    if (targetEmail === 'info@luminalearn.pro' || targetEmail === 'victord70@gmail.com') {
      showFeedback("Cannot modify platform owner.", "error");
      return;
    }
    try {
      await setDoc(doc(db, 'users', targetUserId), { isPlatformAdmin: !currentStatus }, { merge: true });
      await setDoc(doc(collection(db, 'role_audit_logs')), {
        changedUserId: targetUserId,
        changedBy: currentUser.uid,
        oldRole: currentStatus ? 'platformAdmin' : 'none',
        newRole: !currentStatus ? 'platformAdmin' : 'none',
        scope: 'platformAdmin',
        createdAt: serverTimestamp(),
        reason: 'Platform Owner toggled admin rights'
      });
      await fetchAllPlatformData();
      showFeedback("Admin rights updated.");
      if (selectedUser?.id === targetUserId) {
        setSelectedUser({ ...selectedUser, isPlatformAdmin: !currentStatus });
      }
    } catch (e: any) {
      console.error(e?.message || e);
      showFeedback("Failed to modify admin rights", "error");
    }
  };

  const handleUpdateGlobalRole = async (userId: string, targetRole: 'tutor' | 'learner') => {
    try {
      await setDoc(doc(db, 'users', userId), { globalRole: targetRole, role: targetRole }, { merge: true });
      await fetchAllPlatformData();
      showFeedback("Role updated.");
      if (selectedUser?.id === userId) {
        setSelectedUser({ ...selectedUser, globalRole: targetRole, role: targetRole });
      }
    } catch(e: any) {
      console.error("Failed to update role", e?.message || e);
      showFeedback("Failed to update role", "error");
    }
  };

  const handleToggleSuspend = async (userId: string, currentStatus: boolean) => {
    try {
      await setDoc(doc(db, 'users', userId), { status: currentStatus ? 'active' : 'suspended' }, { merge: true });
      await fetchAllPlatformData();
      showFeedback("User suspended status toggled.");
      if (selectedUser?.id === userId) {
        setSelectedUser({ ...selectedUser, status: currentStatus ? 'active' : 'suspended' });
      }
    } catch(e: any) {
      console.error("Failed to suspend/unsuspend", e?.message || e);
      showFeedback("Failed to suspend/unsuspend", "error");
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (confirmDeleteId !== userId) {
      setConfirmDeleteId(userId);
      return;
    }
    
    try {
      if (!currentUser) return;
      await setDoc(doc(db, 'users', userId), {
        status: 'deleted',
        deletedAt: serverTimestamp(),
        deletedBy: currentUser.uid,
      }, { merge: true });

      // Add audit log
      await addDoc(collection(db, 'role_audit_logs'), {
        action: 'delete_user',
        targetUserId: userId,
        performedBy: currentUser.uid,
        timestamp: serverTimestamp(),
        reason: 'Platform admin deleted user'
      });
      
      await fetchAllPlatformData();
      showFeedback("User soft-deleted successfully.");
      setConfirmDeleteId(null);
      if (selectedUser?.id === userId) {
        setSelectedUser(null);
      }
    } catch(e: any) {
      console.error("Failed to delete user", e?.message || e);
      showFeedback("Failed to delete user: " + (e?.message || e), "error");
    }
  };

  const handleUpdatePlan = async (
    scope: 'user' | 'organisation',
    target: any,
    values: { status: PlanStatus; expiresOn: string; limits: PlanLimits }
  ) => {
    if (!currentUser || !target?.id) return;

    setSavingPlan(true);
    try {
      const expiry = values.expiresOn
        ? Timestamp.fromDate(new Date(`${values.expiresOn}T23:59:59.999Z`))
        : null;
      const collectionName = scope === 'user' ? 'users' : 'organisations';
      const updates: any = {
        planStatus: values.status,
        planLimits: values.limits,
        planUpdatedAt: serverTimestamp(),
        planUpdatedBy: currentUser.uid
      };

      if (scope === 'user') {
        updates.planExpiresAt = expiry;
      } else {
        updates.pilotEndDate = expiry;
        if ((values.status === 'trial' || values.status === 'paid_pilot') && !target.pilotStartDate) {
          updates.pilotStartDate = serverTimestamp();
        }
      }

      const batch = writeBatch(db);
      batch.set(doc(db, collectionName, target.id), updates, { merge: true });
      batch.set(doc(collection(db, 'plan_audit_logs')), {
        targetType: scope,
        targetId: target.id,
        previousStatus: resolvePlanStatus(target.planStatus),
        newStatus: values.status,
        previousLimits: resolvePlanLimits(scope, target.planLimits),
        newLimits: values.limits,
        previousExpiry: scope === 'user' ? target.planExpiresAt || null : target.pilotEndDate || null,
        newExpiry: expiry,
        changedBy: currentUser.uid,
        changedByEmail: currentUser.email || null,
        createdAt: serverTimestamp()
      });
      await batch.commit();

      await fetchAllPlatformData();
      const updatedTarget = {
        ...target,
        planStatus: values.status,
        planLimits: values.limits,
        ...(scope === 'user' ? { planExpiresAt: expiry } : { pilotEndDate: expiry })
      };

      if (scope === 'user') {
        setSelectedUser(updatedTarget);
      } else {
        setSelectedOrg(updatedTarget);
      }
      showFeedback(`${scope === 'user' ? 'User' : 'Organisation'} pilot controls updated.`);
    } catch (error: any) {
      console.error('Failed to update pilot controls', error?.message || error);
      showFeedback(error?.message || 'Failed to update pilot controls.', 'error');
    } finally {
      setSavingPlan(false);
    }
  };

  const getTodayKey = () => new Date().toISOString().slice(0, 10);

  const formatFeatureName = (feature?: string) => {
    const value = String(feature || 'unknown');
    return value
      .split('_')
      .map(part => part ? part.charAt(0).toUpperCase() + part.slice(1) : part)
      .join(' ');
  };

  const getUsageStatusClass = (status?: string) => {
    switch (status) {
      case 'succeeded':
        return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
      case 'failed':
      case 'blocked':
        return 'text-rose-400 bg-rose-500/10 border-rose-500/20';
      case 'started':
        return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
      default:
        return 'text-text-alt bg-bg-main border-border-main';
    }
  };

  const getPlanStatusClass = (statusValue?: string) => {
    const status = resolvePlanStatus(statusValue);
    if (status === 'paid_pilot') return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
    if (status === 'trial') return 'text-sky-400 bg-sky-500/10 border-sky-500/20';
    if (status === 'suspended' || status === 'expired') return 'text-rose-400 bg-rose-500/10 border-rose-500/20';
    return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
  };

  const getDisplayedPlanStatus = (target: any, scope: 'user' | 'organisation'): PlanStatus => {
    const status = resolvePlanStatus(target?.planStatus);
    if (status === 'suspended' || status === 'expired') return status;

    const expiryValue = scope === 'user' ? target?.planExpiresAt : target?.pilotEndDate;
    if (!expiryValue) return status;

    try {
      const expiry = typeof expiryValue?.toDate === 'function' ? expiryValue.toDate() : new Date(expiryValue);
      return Number.isFinite(expiry.getTime()) && expiry.getTime() <= Date.now() ? 'expired' : status;
    } catch {
      return status;
    }
  };

  const buildTodayUsageSummary = () => {
    const todayKey = getTodayKey();
    const todayDaily = usageDailyList.filter(item => item.dayKey === todayKey);

    const summary: any = {
      totalEvents: 0,
      statuses: {},
      features: {}
    };

    todayDaily.forEach((item) => {
      summary.totalEvents += Number(item.totalEvents || 0);

      Object.entries(item.statuses || {}).forEach(([status, count]: any) => {
        summary.statuses[status] = (summary.statuses[status] || 0) + Number(count || 0);
      });

      Object.entries(item.features || {}).forEach(([feature, data]: any) => {
        const existing = summary.features[feature] || { eventCount: 0, started: 0, succeeded: 0, failed: 0, blocked: 0 };
        existing.eventCount += Number(data?.eventCount || 0);
        existing.started += Number(data?.started || 0);
        existing.succeeded += Number(data?.succeeded || 0);
        existing.failed += Number(data?.failed || 0);
        existing.blocked += Number(data?.blocked || 0);
        summary.features[feature] = existing;
      });
    });

    return summary;
  };

  const todayUsageSummary = buildTodayUsageSummary();
  const todayUsageEvents = usageEventsList.filter(event => event.dayKey === getTodayKey());
  const getFeatureUsageCount = (feature: string) => todayUsageSummary.features?.[feature]?.eventCount || 0;

  const renderTabs = () => (
    <div className="flex space-x-2 border-b border-border-main mb-8 overflow-x-auto pb-1 relative z-10 w-full" style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
      {[
        { id: 'system', name: 'System Observability', icon: Activity },
        { id: 'jobs', name: 'Generation Jobs', icon: RefreshCw },
        { id: 'feedback', name: 'Pilot Feedback', icon: MessageSquare },
        { id: 'errors', name: 'App Error Logs', icon: AlertTriangle },
        { id: 'users', name: 'User Management', icon: Users },
        { id: 'orgs', name: 'Organizations', icon: Building },
        { id: 'analytics', name: 'Platform Analytics', icon: BarChart3 }
      ].map(tab => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id as any)}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === tab.id 
              ? 'border-indigo-500 text-indigo-400 bg-indigo-500/5' 
              : 'border-transparent text-text-alt hover:text-text-main hover:border-border-main'
          }`}
        >
          <tab.icon className="w-4 h-4" />
          {tab.name}
        </button>
      ))}
    </div>
  );

  return (
    <div className="p-4 sm:p-8 space-y-8 bg-bg-main min-h-screen text-text-main w-full max-w-[100vw] overflow-x-hidden" data-clarity-mask="true">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-text-main">Platform Owner Dashboard</h1>
          <p className="text-text-alt mt-1">Manage users, monitor performance, and analyze platform metrics.</p>
        </div>
        <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/50 text-emerald-500 rounded-full text-sm font-medium flex items-center gap-2 self-start">
          <div className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse" />
          Live Monitoring Active
        </div>
      </div>

      {renderTabs()}

      {activeTab === 'system' && (
        <>
          {error ? (
            <div className="max-w-md mx-auto p-6 bg-rose-500/10 border border-rose-500/30 rounded-2xl text-center">
              <AlertTriangle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-text-main mb-2">Access Issue</h2>
              <p className="text-text-alt mb-6">{error}</p>
              <button 
                onClick={() => window.location.reload()}
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-colors"
              >
                Retry Connection
              </button>
            </div>
          ) : !metrics ? (
            <div className="p-8 text-center text-text-alt">
              <Activity className="h-8 w-8 animate-spin mx-auto mb-4" />
              Loading system metrics...
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard 
                  title="Total Processed" 
                  value={metrics.totalProcessed} 
                  icon={<CheckCircle className="h-5 w-5 text-emerald-500" />} 
                  subtitle={`${metrics.failures} failures detected`}
                />
                <MetricCard 
                  title="Avg. Latency" 
                  value={formatTime(metrics.avgTimeMs)} 
                  icon={<Clock className="h-5 w-5 text-indigo-500" />} 
                  subtitle="Processing time per task"
                />
                <MetricCard 
                  title="Queue Wait" 
                  value={formatTime(metrics.avgWaitTimeMs)} 
                  icon={<BarChart3 className="h-5 w-5 text-amber-500" />} 
                  subtitle="Time spent in queue"
                />
                <MetricCard 
                  title="Queue Size" 
                  value={metrics.queueSize} 
                  icon={<Activity className="h-5 w-5 text-rose-500" />} 
                  subtitle={`${metrics.activeCount} workers active`}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-bg-alt border border-border-main rounded-2xl p-6 shadow-xl overflow-hidden">
                  <h3 className="text-lg font-semibold text-text-main mb-6 flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-indigo-500" />
                    Stage Performance
                  </h3>
                  <div className="space-y-6">
                    {Object.entries(metrics.stageAverages || {}).map(([stage, avg]: [string, any]) => (
                      <div key={stage}>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-text-alt capitalize">{stage.replace('_', ' ')}</span>
                          <span className="text-text-main font-medium">{formatTime(avg)}</span>
                        </div>
                        <div className="w-full bg-bg-main h-2 rounded-full overflow-hidden">
                          <div 
                            className="bg-indigo-500 h-full rounded-full transition-all duration-1000" 
                            style={{ width: `${Math.min((avg / 60000) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-bg-alt border border-border-main rounded-2xl p-6 shadow-xl overflow-hidden">
                  <h3 className="text-lg font-semibold text-text-main mb-6 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-rose-500" />
                    System Health
                  </h3>
                  <div className="space-y-4">
                    <HealthItem label="API Rate Limits" status={metrics.failures < 10 ? 'Healthy' : 'Degraded'} />
                    <HealthItem label="Queue Backpressure" status={metrics.queueSize < 20 ? 'Optimal' : 'High Load'} />
                    <HealthItem label="Retry Convergence" status={metrics.retries < metrics.totalProcessed * 0.2 ? 'Good' : 'Needs Tuning'} />
                    <HealthItem label="Persistence Layer" status="Online" />
                  </div>
                  
                  <div className="mt-8 p-4 bg-bg-main/50 rounded-xl border border-border-main">
                    <p className="text-xs text-text-alt">
                      Last Updated: {metrics.updatedAt?.toDate?.().toLocaleString() || 'Just now'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-bg-alt border border-border-main rounded-2xl p-6 shadow-xl overflow-hidden">
                <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-4">
                  <h3 className="text-lg font-semibold text-text-main flex items-center gap-2">
                    <Users className="h-5 w-5 text-sky-500" />
                    Runtime Identity & Config
                  </h3>
                  <button 
                    onClick={() => {
                      const fetchIdentity = async () => {
                        const btn = document.getElementById('fetch-identity-btn');
                        if (btn) btn.innerText = 'Fetching...';
                        try {
                          const resp = await fetch('/api/runtime-identity');
                          const data = await resp.json();
                          const pre = document.getElementById('identity-output');
                          if (pre) pre.innerText = safeStringify(data, null, 2);
                        } catch (e: any) {
                          const pre = document.getElementById('identity-output');
                          if (pre) pre.innerText = `Error: ${e.message}`;
                        } finally {
                          if (btn) btn.innerText = 'Refresh Identity';
                        }
                      };
                      fetchIdentity();
                    }}
                    id="fetch-identity-btn"
                    className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                  >
                    Check Runtime Identity
                  </button>
                </div>
                
                <div className="bg-bg-main rounded-xl p-4 border border-border-main overflow-x-auto">
                  <pre id="identity-output" className="text-xs font-mono text-sky-500 whitespace-pre-wrap break-words">
                    Click the button above to run backend diagnostics...
                  </pre>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {activeTab === 'jobs' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-bold text-text-main flex items-center gap-2">
                <RefreshCw className="text-indigo-500 w-6 h-6" />
                Generation Jobs (Pilot)
              </h2>
              <p className="text-sm text-text-alt mt-1">Review AI generation tasks.</p>
            </div>
            <span className="text-xs bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">
              {jobsList.length} jobs
            </span>
          </div>

          {jobsLoading ? (
            <div className="p-12 text-center text-text-alt">
              <Activity className="h-8 w-8 animate-spin mx-auto mb-4" />
              Loading jobs...
            </div>
          ) : jobsList.length === 0 ? (
            <div className="p-12 text-center bg-bg-alt border border-border-main rounded-2xl text-text-alt h-48 flex flex-col items-center justify-center">
              <RefreshCw className="w-8 h-8 mb-2 opacity-55" />
              No jobs submitted yet.
            </div>
          ) : (
            <div className="overflow-x-auto w-full">
              <table className="w-full text-left text-sm whitespace-nowrap min-w-[800px]">
                <thead>
                  <tr className="border-b border-border-main text-text-alt">
                    <th className="py-4 px-2 tracking-wider uppercase text-xs">Date</th>
                    <th className="py-4 px-2 tracking-wider uppercase text-xs">User/Org ID</th>
                    <th className="py-4 px-2 tracking-wider uppercase text-xs">Status</th>
                    <th className="py-4 px-2 tracking-wider uppercase text-xs">Progress</th>
                    <th className="py-4 px-2 tracking-wider uppercase text-xs w-[30%]">Details</th>
                    <th className="py-4 px-2 tracking-wider uppercase text-xs text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-main text-text-main">
                  {jobsList.map(job => (
                    <React.Fragment key={job.id}>
                      <tr className="hover:bg-bg-alt/50 transition-colors cursor-pointer" onClick={() => toggleExpandedJob(job.id)}>
                        <td className="py-3 px-2">
                          {job.createdAt?.toDate ? job.createdAt.toDate().toLocaleString() : 'N/A'}
                        </td>
                        <td className="py-3 px-2 font-mono text-xs">
                          <span className="text-text-main">{job.userId?.slice(0, 8)}...</span>
                          {job.organisationId && <span className="text-text-alt block">Org: {job.organisationId.slice(0, 8)}...</span>}
                        </td>
                        <td className="py-3 px-2">
                           <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                              job.status === 'failed' || job.status === 'timed_out' ? 'text-red-400 bg-red-400/10' :
                              job.status === 'completed' || job.status === 'review_ready' ? 'text-green-400 bg-green-400/10' :
                              'text-amber-400 bg-amber-400/10'
                            }`}>
                            {job.status}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <div className="text-xs text-text-alt">
                            <div>{job.progress || 0}%</div>
                            <div className="text-[10px] opacity-70">{job.currentStep || 'queued'}</div>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <div className="max-w-xs truncate text-xs" title={job?.errorMessage || job?.error || job?.message}>
                            <div className="text-text-alt">{job.message || 'Processing'}</div>
                            <div className="text-[10px] text-text-alt/70">Attempt {job.attemptCount || 0}/{job.maxAttempts || 3}</div>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-right">
                          {canRetryJob(job) ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleRetryJob(job); }}
                              disabled={retryingJobId === job.id}
                              className="px-3 py-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20 text-xs font-medium hover:bg-amber-500/20 disabled:opacity-60"
                            >
                              {retryingJobId === job.id ? 'Retrying…' : 'Retry'}
                            </button>
                          ) : (
                            <span className="text-[11px] text-text-alt/70">No action</span>
                          )}
                        </td>
                      </tr>
                      {expandedJobIds.includes(job.id) && (
                        <tr>
                          <td colSpan={6} className="px-2 pb-4">
                            <div className="rounded-xl border border-border-main bg-bg-main/70 p-4 text-xs text-text-main space-y-2">
                              <div className="font-semibold text-text-alt uppercase tracking-wider">Job Details</div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {[
                                  ['jobId', job.jobId || job.id],
                                  ['jobType', job.jobType],
                                  ['intent', job.intent],
                                  ['status', job.status],
                                  ['currentStep', job.currentStep],
                                  ['progress', job.progress],
                                  ['message', job.message],
                                  ['sourceIds', job.sourceIds?.join(', ')],
                                  ['storagePaths', job.storagePaths?.join(', ')],
                                  ['sources', safeStringify(job.sources)],
                                  ['attemptCount', job.attemptCount],
                                  ['maxAttempts', job.maxAttempts],
                                  ['createdAt', job.createdAt?.toDate ? job.createdAt.toDate().toISOString() : job.createdAt],
                                  ['updatedAt', job.updatedAt?.toDate ? job.updatedAt.toDate().toISOString() : job.updatedAt],
                                  ['lastHeartbeatAt', job.lastHeartbeatAt?.toDate ? job.lastHeartbeatAt.toDate().toISOString() : job.lastHeartbeatAt],
                                  ['startedAt', job.startedAt?.toDate ? job.startedAt.toDate().toISOString() : job.startedAt],
                                  ['completedAt', job.completedAt?.toDate ? job.completedAt.toDate().toISOString() : job.completedAt],
                                  ['failedAt', job.failedAt?.toDate ? job.failedAt.toDate().toISOString() : job.failedAt],
                                  ['errorCode', job.errorCode],
                                  ['errorMessage', job.errorMessage || job.error]
                                ].map(([label, value]) => (
                                  <div key={label} className="break-words">
                                    <div className="text-[10px] uppercase tracking-wider text-text-alt">{label}</div>
                                    <div className="mt-1 whitespace-pre-wrap">{value ?? '—'}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'feedback' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-bold text-text-main flex items-center gap-2">
                <MessageSquare className="text-indigo-500 w-6 h-6" />
                Pilot Participant Feedback
              </h2>
              <p className="text-sm text-text-alt mt-1">Review testing feedback from your pilot users.</p>
            </div>
            <span className="text-xs bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">
              {feedbackList.length} submissions
            </span>
          </div>

          {feedbackLoading ? (
            <div className="p-12 text-center text-text-alt">
              <Activity className="h-8 w-8 animate-spin mx-auto mb-4" />
              Loading pilot feedback...
            </div>
          ) : feedbackList.length === 0 ? (
            <div className="p-12 text-center bg-bg-alt border border-border-main rounded-2xl text-text-alt h-48 flex flex-col items-center justify-center">
              <MessageSquare className="w-8 h-8 mb-2 opacity-55" />
              No feedback submissions received yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {feedbackList.map((fb) => (
                <div key={fb.id} className="bg-bg-alt border border-border-main rounded-2xl p-6 shadow-md hover:border-indigo-500/30 transition-all flex flex-col justify-between">
                  <div>
                    <div className="flex flex-wrap justify-between items-start gap-2 mb-4 border-b border-border-main pb-3">
                      <div>
                        <span className="text-[10px] bg-indigo-500/10 text-indigo-400 font-bold px-2 py-0.5 rounded-md uppercase tracking-wider">
                          Pilot User
                        </span>
                        <div className="text-sm font-bold text-text-main mt-1.5 flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-text-alt" /> {fb.userEmail || fb.email || 'anonymous'}
                        </div>
                        {fb.organisationId && (
                          <div className="text-xs text-text-alt mt-1 flex items-center gap-1">
                            <Building className="w-3 h-3 text-indigo-400" /> Org: <span className="font-mono">{fb.organisationId}</span>
                          </div>
                        )}
                        {fb.userId && (
                          <div className="text-[10px] text-text-alt opacity-60 font-mono mt-0.5">
                            UID: {fb.userId}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-text-alt flex items-center gap-1 justify-end font-mono">
                          <Clock className="w-3 h-3 text-text-alt" /> {formatTimestamp(fb.createdAt)}
                        </div>
                        <div className="text-xs text-text-alt mt-1 flex items-center gap-1 justify-end capitalize">
                          <Monitor className="w-3 h-3 text-text-alt" /> {fb.deviceType || 'unknown'}
                        </div>
                        {fb.route && (
                          <div className="text-xs text-indigo-400 mt-1 font-mono">
                            {fb.route}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-4">
                      {fb.tryingToDo && (
                        <div>
                          <h4 className="text-xs font-semibold text-text-alt uppercase tracking-wider mb-1">🎯 What they were trying to do</h4>
                          <p className="text-sm text-text-main pl-3 border-l-2 border-indigo-500/20">{fb.tryingToDo}</p>
                        </div>
                      )}
                      
                      {fb.workedWell && (
                        <div>
                          <h4 className="text-xs font-semibold text-text-alt uppercase tracking-wider mb-1">👍 What worked well</h4>
                          <p className="text-sm text-emerald-400 pl-3 border-l-2 border-emerald-500/20">{fb.workedWell}</p>
                        </div>
                      )}

                      {fb.brokeOrConfused && (
                        <div>
                          <h4 className="text-xs font-semibold text-text-alt uppercase tracking-wider mb-1">⚠️ What broke or confused them</h4>
                          <p className="text-sm text-rose-400 pl-3 border-l-2 border-rose-500/20">{fb.brokeOrConfused}</p>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4 pt-3 border-t border-border-main/50 text-xs">
                        {fb.matchedDocument && (
                          <div>
                            <span className="text-text-alt font-medium block">Match Document?</span>
                            <span className="font-semibold text-text-main">{fb.matchedDocument}</span>
                          </div>
                        )}
                        {fb.tutorAnsweredCorrectly && (
                          <div>
                            <span className="text-text-alt font-medium block">Tutor Answer Correct?</span>
                            <span className="font-semibold text-text-main">{fb.tutorAnsweredCorrectly}</span>
                          </div>
                        )}
                        {fb.useForTraining && (
                          <div className="col-span-2 border-t border-border-main/30 pt-2">
                            <span className="text-text-alt font-medium block">Would use for training?</span>
                            <span className="font-semibold text-text-main">{fb.useForTraining}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'errors' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-bold text-text-main flex items-center gap-2">
                <ShieldAlert className="text-rose-500 w-6 h-6" />
                Runtime Error Logs
              </h2>
              <p className="text-sm text-text-alt mt-1">Lightweight app stability tracking for pilot MVPs</p>
            </div>
            <span className="text-xs bg-rose-500/10 text-rose-400 border border-rose-500/20 font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">
              {errorsList.length} errors cached
            </span>
          </div>

          {errorsLoading ? (
            <div className="p-12 text-center text-text-alt">
              <Activity className="h-8 w-8 animate-spin mx-auto mb-4" />
              Loading app error logs...
            </div>
          ) : errorsList.length === 0 ? (
            <div className="p-12 text-center bg-bg-alt border border-border-main rounded-2xl text-text-alt h-48 flex flex-col items-center justify-center">
              <CheckCircle className="w-8 h-8 mb-2 text-emerald-500 opacity-75" />
              No errors detected so far. System is stable!
            </div>
          ) : (
            <div className="bg-bg-alt border border-border-main rounded-2xl overflow-hidden shadow-xl">
              <div className="divide-y divide-border-main overflow-x-auto w-full">
                <table className="w-full text-left border-collapse min-w-[900px]">
                  <thead>
                    <tr className="bg-bg-main border-b border-border-main text-xs font-semibold text-text-alt uppercase tracking-wider">
                      <th className="p-4">Time</th>
                      <th className="p-4">Failed Action</th>
                      <th className="p-4">Error Message</th>
                      <th className="p-4">User Details</th>
                      <th className="p-4">Context / Route</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-main text-sm">
                    {errorsList.map((err) => (
                      <tr key={err.id} className="hover:bg-bg-main/30 transition-colors">
                        <td className="p-4 text-xs font-mono text-text-alt whitespace-nowrap">
                          {formatTimestamp(err.timestamp)}
                        </td>
                        <td className="p-4 font-semibold text-rose-400 uppercase tracking-wide text-xs whitespace-nowrap">
                          {err.action || 'general_failure'}
                        </td>
                        <td className="p-4 max-w-sm font-mono text-xs whitespace-pre-wrap break-all text-text-main">
                          {err.errorMessage || 'Unknown error details'}
                        </td>
                        <td className="p-4 text-xs whitespace-nowrap">
                          {err.userEmail ? (
                            <div className="font-bold text-text-main flex items-center gap-1"><Mail className="w-3 h-3 text-indigo-400" />{err.userEmail}</div>
                          ) : (
                            <div className="text-text-alt italic">Anonymous user</div>
                          )}
                          {err.userId && (
                            <div className="text-[10px] text-text-alt mt-1">UID: {err.userId}</div>
                          )}
                        </td>
                        <td className="p-4 text-xs max-w-xs">
                          {err.route ? (
                            <div className="font-mono text-indigo-400 truncate" title={err.route}>
                              {err.route}
                            </div>
                          ) : (
                            <div className="text-text-alt italic">Unknown context</div>
                          )}
                          <div className="text-text-alt opacity-70 mt-1 flex items-center gap-1 text-[10px] truncate max-w-[200px]">
                            <Monitor className="w-2.5 h-2.5" />
                            {err.deviceType || 'unknown'} | {err.browser ? err.browser.substring(0, 40) + '...' : 'unknown'}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'users' && (
        <div className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h2 className="text-2xl font-bold text-text-main">Registered Users</h2>
              <p className="text-sm text-text-alt">Manage individual platform users, tutors, and students.</p>
            </div>
            <button 
              onClick={fetchAllPlatformData} 
              className="px-4 py-2 bg-bg-alt border border-border-main rounded-lg text-text-main text-sm hover:bg-bg-main transition-colors"
            >
              Refresh List
            </button>
          </div>
          
          {isLoadingData ? (
            <div className="p-12 text-center text-text-alt"><Activity className="h-8 w-8 animate-spin mx-auto mb-4" />Loading...</div>
          ) : (
            <div className="bg-bg-alt border border-border-main rounded-xl overflow-hidden overflow-x-auto w-full">
              <table className="w-full text-left border-collapse min-w-[800px]">
                <thead>
                  <tr className="bg-bg-main border-b border-border-main">
                    <th className="p-4 text-sm font-medium text-text-alt">User ID</th>
                    <th className="p-4 text-sm font-medium text-text-alt">Email</th>
                    <th className="p-4 text-sm font-medium text-text-alt">Name</th>
                    <th className="p-4 text-sm font-medium text-text-alt">Role / Orgs</th>
                    <th className="p-4 text-sm font-medium text-text-alt">Pilot Plan</th>
                    <th className="p-4 text-sm font-medium text-text-alt text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-main">
                  {usersList.length === 0 ? (
                    <tr><td colSpan={6} className="p-8 text-center text-text-alt">No users found.</td></tr>
                  ) : (
                    usersList.map((usr, i) => (
                      <tr key={i} className="hover:bg-bg-main/50 transition-colors">
                        <td className="p-4 text-xs font-mono text-text-alt">{usr.id}</td>
                        <td className="p-4 text-sm text-text-main">{usr.email || 'N/A'}</td>
                        <td className="p-4 text-sm text-text-main">{usr.displayName || usr.firstName || 'Unknown'}</td>
                        <td className="p-4 text-sm text-text-alt">
                          Global: <span className="capitalize">{usr.globalRole || usr.role || 'none'}</span>
                          {usr.isPlatformAdmin && <span className="ml-2 px-2 py-0.5 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-full text-[10px] uppercase font-bold tracking-wider">Sys Admin</span>}
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex px-2 py-1 rounded-md border text-[10px] font-bold uppercase tracking-wider ${getPlanStatusClass(getDisplayedPlanStatus(usr, 'user'))}`}>
                            {formatPlanStatus(getDisplayedPlanStatus(usr, 'user'))}
                          </span>
                        </td>
                        <td className="p-4 text-right flex justify-end gap-2">
                          <button 
                            onClick={() => setSelectedUser(usr)}
                            className="text-xs text-indigo-400 hover:text-indigo-300 px-2 py-1"
                          >
                            View
                          </button>
                          {isPlatformOwner && (
                            <button
                              onClick={() => togglePlatformAdmin(usr.id, !!usr.isPlatformAdmin, usr.email)}
                              className={`text-xs px-2 py-1 rounded transition-colors ${
                                usr.isPlatformAdmin 
                                  ? 'bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 border border-rose-500/20' 
                                  : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20'
                              }`}
                            >
                              {usr.isPlatformAdmin ? 'Demote Admin' : 'Make Admin'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'orgs' && (
        <div className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h2 className="text-2xl font-bold text-text-main">Organizations</h2>
              <p className="text-sm text-text-alt">Manage enterprise clients and coaching groups.</p>
            </div>
          </div>
          
          {isLoadingData ? (
            <div className="p-12 text-center text-text-alt"><Activity className="h-8 w-8 animate-spin mx-auto mb-4" />Loading...</div>
          ) : (
            <div className="bg-bg-alt border border-border-main rounded-xl overflow-hidden overflow-x-auto w-full">
              <table className="w-full text-left border-collapse min-w-[800px]">
                <thead>
                  <tr className="bg-bg-main border-b border-border-main">
                    <th className="p-4 text-sm font-medium text-text-alt">Org ID</th>
                    <th className="p-4 text-sm font-medium text-text-alt">Name</th>
                    <th className="p-4 text-sm font-medium text-text-alt">Owner ID</th>
                    <th className="p-4 text-sm font-medium text-text-alt">Pilot Plan</th>
                    <th className="p-4 text-sm font-medium text-text-alt text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-main">
                  {orgsList.length === 0 ? (
                    <tr><td colSpan={5} className="p-8 text-center text-text-alt">No organizations found.</td></tr>
                  ) : (
                    orgsList.map((org, i) => (
                      <tr key={i} className="hover:bg-bg-main/50 transition-colors">
                        <td className="p-4 text-xs font-mono text-text-alt">{org.id}</td>
                        <td className="p-4 text-sm text-text-main font-bold">{org.name}</td>
                        <td className="p-4 text-xs font-mono text-text-alt">{org.ownerUserId}</td>
                        <td className="p-4">
                          <span className={`inline-flex px-2 py-1 rounded-md border text-[10px] font-bold uppercase tracking-wider ${getPlanStatusClass(getDisplayedPlanStatus(org, 'organisation'))}`}>
                            {formatPlanStatus(getDisplayedPlanStatus(org, 'organisation'))}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <button onClick={() => setSelectedOrg(org)} className="text-xs text-indigo-400 hover:text-indigo-300">Manage</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="space-y-8">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-bold text-text-main flex items-center gap-2">
                <BarChart3 className="text-indigo-500 w-6 h-6" />
                Platform Analytics
              </h2>
              <p className="text-sm text-text-alt mt-1">
                Monitor platform growth, AI usage, and pilot activity.
              </p>
            </div>
            <span className="text-xs bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">
              Usage today: {todayUsageSummary.totalEvents || 0}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard 
              title="Total Users" 
              value={usersList.length} 
              icon={<Users className="h-5 w-5 text-sky-500" />} 
              subtitle="Registered accounts"
            />
            <MetricCard 
              title="Organizations" 
              value={orgsList.length} 
              icon={<Building className="h-5 w-5 text-indigo-500" />} 
              subtitle="Active enterprise groups"
            />
            <MetricCard 
              title="Published Courses" 
              value={coursesList.length} 
              icon={<BookOpen className="h-5 w-5 text-emerald-500" />} 
              subtitle="Modules created by tutors"
            />
            <MetricCard 
              title="Total Enrollments" 
              value={enrollmentsList.length} 
              icon={<GraduationCap className="h-5 w-5 text-amber-500" />} 
              subtitle="Course participation"
            />
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-bold text-text-main">AI Usage Today</h3>
              <p className="text-sm text-text-alt mt-1">
                Tracks server-side AI activity from Ask Source, summaries, and course generation.
              </p>
            </div>

            {usageLoading ? (
              <div className="p-8 text-center text-text-alt bg-bg-alt border border-border-main rounded-2xl">
                <Activity className="h-8 w-8 animate-spin mx-auto mb-4" />
                Loading usage data...
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard
                  title="Total AI Events"
                  value={todayUsageSummary.totalEvents || 0}
                  icon={<Activity className="h-5 w-5 text-indigo-500" />}
                  subtitle={`${todayUsageSummary.statuses?.succeeded || 0} succeeded, ${todayUsageSummary.statuses?.failed || 0} failed`}
                />
                <MetricCard
                  title="Ask Source"
                  value={getFeatureUsageCount('ask_source')}
                  icon={<MessageSquare className="h-5 w-5 text-sky-500" />}
                  subtitle="Questions and answers"
                />
                <MetricCard
                  title="Source Summaries"
                  value={getFeatureUsageCount('source_set_summary') + getFeatureUsageCount('document_brief')}
                  icon={<BookOpen className="h-5 w-5 text-emerald-500" />}
                  subtitle="Summary jobs and completions"
                />
                <MetricCard
                  title="Create Learning"
                  value={getFeatureUsageCount('source_set_course_generation') + getFeatureUsageCount('course_generation')}
                  icon={<GraduationCap className="h-5 w-5 text-amber-500" />}
                  subtitle="Course generation jobs"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {['started', 'succeeded', 'failed', 'blocked'].map((status) => (
                <div key={status} className="bg-bg-alt border border-border-main rounded-2xl p-5">
                  <div className="text-xs uppercase tracking-wider text-text-alt font-bold">{status}</div>
                  <div className="text-2xl font-bold text-text-main mt-2">{todayUsageSummary.statuses?.[status] || 0}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-bg-alt border border-border-main rounded-2xl p-6 overflow-hidden">
              <div className="flex justify-between items-center gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-bold text-text-main">Usage by User Today</h3>
                  <p className="text-xs text-text-alt mt-1">Daily counters from usageDaily.</p>
                </div>
                <span className="text-xs text-text-alt">{usageDailyList.filter(item => item.dayKey === getTodayKey()).length} users</span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm whitespace-nowrap min-w-[620px]">
                  <thead>
                    <tr className="border-b border-border-main text-text-alt">
                      <th className="py-3 px-2 uppercase text-xs">User ID</th>
                      <th className="py-3 px-2 uppercase text-xs">Events</th>
                      <th className="py-3 px-2 uppercase text-xs">Succeeded</th>
                      <th className="py-3 px-2 uppercase text-xs">Failed</th>
                      <th className="py-3 px-2 uppercase text-xs">Top Features</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-main text-text-main">
                    {usageDailyList.filter(item => item.dayKey === getTodayKey()).slice(0, 20).map((item) => {
                      const featureEntries = Object.entries(item.features || {})
                        .sort((a: any, b: any) => Number(b[1]?.eventCount || 0) - Number(a[1]?.eventCount || 0))
                        .slice(0, 2);

                      return (
                        <tr key={item.id} className="hover:bg-bg-main/50">
                          <td className="py-3 px-2 font-mono text-xs">{item.userId?.slice(0, 12) || 'unknown'}...</td>
                          <td className="py-3 px-2 font-bold">{item.totalEvents || 0}</td>
                          <td className="py-3 px-2 text-emerald-400">{item.statuses?.succeeded || 0}</td>
                          <td className="py-3 px-2 text-rose-400">{item.statuses?.failed || 0}</td>
                          <td className="py-3 px-2 text-xs text-text-alt">
                            {featureEntries.length === 0 ? '—' : featureEntries.map(([feature, data]: any) => `${formatFeatureName(feature)}: ${data?.eventCount || 0}`).join(', ')}
                          </td>
                        </tr>
                      );
                    })}
                    {usageDailyList.filter(item => item.dayKey === getTodayKey()).length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-text-alt">No usage recorded today.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-bg-alt border border-border-main rounded-2xl p-6 overflow-hidden">
              <div className="flex justify-between items-center gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-bold text-text-main">Organisation Usage Today</h3>
                  <p className="text-xs text-text-alt mt-1">Populates when AI actions include an organisation ID.</p>
                </div>
                <span className="text-xs text-text-alt">{organisationUsageDailyList.filter(item => item.dayKey === getTodayKey()).length} orgs</span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm whitespace-nowrap min-w-[620px]">
                  <thead>
                    <tr className="border-b border-border-main text-text-alt">
                      <th className="py-3 px-2 uppercase text-xs">Organisation ID</th>
                      <th className="py-3 px-2 uppercase text-xs">Events</th>
                      <th className="py-3 px-2 uppercase text-xs">Succeeded</th>
                      <th className="py-3 px-2 uppercase text-xs">Failed</th>
                      <th className="py-3 px-2 uppercase text-xs">Top Features</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-main text-text-main">
                    {organisationUsageDailyList.filter(item => item.dayKey === getTodayKey()).slice(0, 20).map((item) => {
                      const featureEntries = Object.entries(item.features || {})
                        .sort((a: any, b: any) => Number(b[1]?.eventCount || 0) - Number(a[1]?.eventCount || 0))
                        .slice(0, 2);

                      return (
                        <tr key={item.id} className="hover:bg-bg-main/50">
                          <td className="py-3 px-2 font-mono text-xs">{item.organisationId?.slice(0, 12) || 'unknown'}...</td>
                          <td className="py-3 px-2 font-bold">{item.totalEvents || 0}</td>
                          <td className="py-3 px-2 text-emerald-400">{item.statuses?.succeeded || 0}</td>
                          <td className="py-3 px-2 text-rose-400">{item.statuses?.failed || 0}</td>
                          <td className="py-3 px-2 text-xs text-text-alt">
                            {featureEntries.length === 0 ? '—' : featureEntries.map(([feature, data]: any) => `${formatFeatureName(feature)}: ${data?.eventCount || 0}`).join(', ')}
                          </td>
                        </tr>
                      );
                    })}
                    {organisationUsageDailyList.filter(item => item.dayKey === getTodayKey()).length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-text-alt">No organisation usage recorded today.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="bg-bg-alt border border-border-main rounded-2xl p-6 overflow-hidden">
            <div className="flex justify-between items-center gap-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-text-main">Recent Usage Events</h3>
                <p className="text-xs text-text-alt mt-1">Latest server-side AI usage records.</p>
              </div>
              <span className="text-xs bg-bg-main border border-border-main text-text-alt px-3 py-1.5 rounded-full">
                {usageEventsList.length} recent events
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap min-w-[920px]">
                <thead>
                  <tr className="border-b border-border-main text-text-alt">
                    <th className="py-3 px-2 uppercase text-xs">Time</th>
                    <th className="py-3 px-2 uppercase text-xs">Feature</th>
                    <th className="py-3 px-2 uppercase text-xs">Status</th>
                    <th className="py-3 px-2 uppercase text-xs">Action</th>
                    <th className="py-3 px-2 uppercase text-xs">User</th>
                    <th className="py-3 px-2 uppercase text-xs">Source Set</th>
                    <th className="py-3 px-2 uppercase text-xs">Model</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-main text-text-main">
                  {usageEventsList.slice(0, 40).map((event) => (
                    <tr key={event.id} className="hover:bg-bg-main/50">
                      <td className="py-3 px-2 text-xs text-text-alt">
                        {event.createdAt?.toDate ? event.createdAt.toDate().toLocaleString() : 'N/A'}
                      </td>
                      <td className="py-3 px-2 text-xs font-medium">{formatFeatureName(event.feature)}</td>
                      <td className="py-3 px-2">
                        <span className={`px-2 py-1 rounded-md border text-[10px] font-bold uppercase tracking-wider ${getUsageStatusClass(event.status)}`}>
                          {event.status || 'unknown'}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-xs text-text-alt">{event.action || '—'}</td>
                      <td className="py-3 px-2 font-mono text-xs">{event.userId ? `${event.userId.slice(0, 10)}...` : '—'}</td>
                      <td className="py-3 px-2 font-mono text-xs">{event.sourceSetId ? `${event.sourceSetId.slice(0, 10)}...` : '—'}</td>
                      <td className="py-3 px-2 text-xs text-text-alt">
                        {event.modelUsed || '—'}
                        {event.fallbackUsed ? <span className="block text-amber-400">fallback used</span> : null}
                      </td>
                    </tr>
                  ))}
                  {usageEventsList.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-text-alt">No usage events recorded yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {selectedUser && (
        <div className="fixed inset-0 z-50 bg-bg-main/90 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in duration-300">
          <div className="bg-bg-alt border border-border-main rounded-3xl p-8 max-w-2xl w-full shadow-2xl relative max-h-[90vh] overflow-y-auto">
            <button 
              onClick={() => setSelectedUser(null)}
              className="absolute top-6 right-6 p-2 rounded-full hover:bg-bg-main text-text-alt hover:text-text-main transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-bold text-text-main mb-6">User Management</h2>
            
            {actionFeedback && (
              <div className={`p-4 rounded-xl mb-6 text-sm font-medium ${actionFeedback.type === 'error' ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'}`}>
                {actionFeedback.message}
              </div>
            )}

            <div className="space-y-6">
              <div className="bg-bg-main/50 p-4 rounded-xl border border-border-main space-y-2">
                <p className="text-sm"><span className="text-text-alt font-medium w-24 inline-block">User ID:</span> <span className="font-mono text-indigo-400">{selectedUser.id}</span></p>
                <p className="text-sm"><span className="text-text-alt font-medium w-24 inline-block">Email:</span> {selectedUser.email || 'N/A'}</p>
                <p className="text-sm"><span className="text-text-alt font-medium w-24 inline-block">Name:</span> {selectedUser.displayName || selectedUser.firstName || 'Unknown'}</p>
                <p className="text-sm"><span className="text-text-alt font-medium w-24 inline-block">Global Role:</span> <span className="capitalize">{selectedUser.globalRole || selectedUser.role || 'none'}</span></p>
                <p className="text-sm"><span className="text-text-alt font-medium w-24 inline-block">Status:</span> 
                  <span className={`capitalize ml-1 ${selectedUser.status === 'suspended' ? 'text-rose-500' : 'text-emerald-500'}`}>
                    {selectedUser.status || 'active'}
                  </span>
                </p>
              </div>

              <div key={`user-${selectedUser.id}-${selectedUser.planUpdatedAt?.seconds || 'default'}`}>
                <PlanControls
                  scope="user"
                  target={selectedUser}
                  saving={savingPlan}
                  onSave={(values) => handleUpdatePlan('user', selectedUser, values)}
                />
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-text-main">Actions</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {isPlatformOwner && (
                    <button
                      onClick={() => togglePlatformAdmin(selectedUser.id, !!selectedUser.isPlatformAdmin, selectedUser.email)}
                      className={`flex flex-col items-start p-4 rounded-xl border transition-colors ${
                        selectedUser.isPlatformAdmin 
                          ? 'border-rose-500/30 bg-rose-500/5 hover:bg-rose-500/10' 
                          : 'border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10'
                      }`}
                    >
                      <span className={`font-bold mb-1 ${selectedUser.isPlatformAdmin ? 'text-rose-400' : 'text-emerald-400'}`}>
                        {selectedUser.isPlatformAdmin ? 'Revoke Platform Admin' : 'Grant Platform Admin'}
                      </span>
                      <span className="text-xs text-text-alt text-left">
                        {selectedUser.isPlatformAdmin ? 'Remove full system access from this user.' : 'Grant full system observability and settings access.'}
                      </span>
                    </button>
                  )}

                  <button
                    onClick={() => handleUpdateGlobalRole(selectedUser.id, (selectedUser.globalRole || selectedUser.role) === 'tutor' ? 'learner' : 'tutor')}
                    className="flex flex-col items-start p-4 rounded-xl border border-indigo-500/30 bg-indigo-500/5 hover:bg-indigo-500/10 transition-colors"
                  >
                    <span className="font-bold text-indigo-400 mb-1">
                      Toggle Global Role
                    </span>
                    <span className="text-xs text-text-alt text-left">
                      Switch between Tutor and Learner system capabilities.
                    </span>
                  </button>

                  <button
                    onClick={() => handleToggleSuspend(selectedUser.id, selectedUser.status !== 'suspended')}
                    className={`flex flex-col items-start p-4 rounded-xl border transition-colors ${
                      selectedUser.status === 'suspended'
                        ? 'border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10' 
                        : 'border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10'
                    }`}
                  >
                    <span className={`font-bold mb-1 ${selectedUser.status === 'suspended' ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {selectedUser.status === 'suspended' ? 'Unsuspend User' : 'Suspend User'}
                    </span>
                    <span className="text-xs text-text-alt text-left">
                      {selectedUser.status === 'suspended' ? 'Restore their platform access.' : 'Block login without deleting data.'}
                    </span>
                  </button>

                  <button
                    onClick={() => handleDeleteUser(selectedUser.id)}
                    className="flex flex-col items-start p-4 rounded-xl border border-rose-500/30 bg-rose-500/5 hover:bg-rose-500/10 transition-colors sm:col-span-2"
                  >
                    <span className="font-bold text-rose-400 mb-1">
                      {confirmDeleteId === selectedUser.id ? 'Are you sure? Click again to permanently delete.' : 'Delete User Record'}
                    </span>
                    <span className="text-xs text-text-alt text-left">
                      {confirmDeleteId === selectedUser.id 
                        ? 'This action cannot be undone and will permanently wipe the profile.' 
                        : 'Permanently wipe this user\'s profile document. Use with caution.'}
                    </span>
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {selectedOrg && (
        <div className="fixed inset-0 z-50 bg-bg-main/90 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in duration-300">
          <div className="bg-bg-alt border border-border-main rounded-3xl p-8 max-w-2xl w-full shadow-2xl relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setSelectedOrg(null)}
              className="absolute top-6 right-6 p-2 rounded-full hover:bg-bg-main text-text-alt hover:text-text-main transition-colors"
              aria-label="Close organisation management"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-bold text-text-main mb-6">Organisation Management</h2>

            {actionFeedback && (
              <div className={`p-4 rounded-xl mb-6 text-sm font-medium ${actionFeedback.type === 'error' ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'}`}>
                {actionFeedback.message}
              </div>
            )}

            <div className="space-y-6">
              <div className="bg-bg-main/50 p-4 rounded-xl border border-border-main space-y-2">
                <p className="text-sm"><span className="text-text-alt font-medium w-28 inline-block">Organisation:</span> {selectedOrg.name || 'Unnamed'}</p>
                <p className="text-sm"><span className="text-text-alt font-medium w-28 inline-block">Org ID:</span> <span className="font-mono text-indigo-400">{selectedOrg.id}</span></p>
                <p className="text-sm"><span className="text-text-alt font-medium w-28 inline-block">Owner ID:</span> <span className="font-mono text-text-alt">{selectedOrg.ownerUserId || 'N/A'}</span></p>
              </div>

              <div key={`organisation-${selectedOrg.id}-${selectedOrg.planUpdatedAt?.seconds || 'default'}`}>
                <PlanControls
                  scope="organisation"
                  target={selectedOrg}
                  saving={savingPlan}
                  onSave={(values) => handleUpdatePlan('organisation', selectedOrg, values)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function toDateInput(value: any): string {
  if (!value) return '';

  try {
    const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
    return Number.isFinite(date.getTime()) ? date.toISOString().slice(0, 10) : '';
  } catch {
    return '';
  }
}

function PlanControls({
  scope,
  target,
  saving,
  onSave
}: {
  scope: 'user' | 'organisation';
  target: any;
  saving: boolean;
  onSave: (values: { status: PlanStatus; expiresOn: string; limits: PlanLimits }) => Promise<void>;
}) {
  const defaultLimits = scope === 'user' ? DEFAULT_USER_PLAN_LIMITS : DEFAULT_ORGANISATION_PLAN_LIMITS;
  const [status, setStatus] = useState<PlanStatus>(resolvePlanStatus(target.planStatus));
  const [expiresOn, setExpiresOn] = useState(toDateInput(scope === 'user' ? target.planExpiresAt : target.pilotEndDate));
  const [limits, setLimits] = useState<PlanLimits>(resolvePlanLimits(scope, target.planLimits || defaultLimits));

  const updateLimit = (field: keyof PlanLimits, value: string) => {
    const numericValue = Number(value);
    setLimits(previous => ({
      ...previous,
      [field]: Number.isFinite(numericValue) && numericValue >= 0 ? Math.floor(numericValue) : 0
    }));
  };

  return (
    <div className="border border-indigo-500/20 bg-indigo-500/5 rounded-2xl p-5 space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-text-main">Pilot Account Controls</h3>
        <p className="text-xs text-text-alt mt-1">
          Active plans use the standard daily profile unless these account values are customised. Zero blocks that action.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="space-y-1.5 text-sm text-text-alt">
          <span className="block font-medium">Plan status</span>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as PlanStatus)}
            className="w-full rounded-lg border border-border-main bg-bg-main px-3 py-2.5 text-text-main"
          >
            {PLAN_STATUSES.map(planStatus => (
              <option key={planStatus} value={planStatus}>{formatPlanStatus(planStatus)}</option>
            ))}
          </select>
        </label>

        <label className="space-y-1.5 text-sm text-text-alt">
          <span className="block font-medium">Expires at end of day</span>
          <input
            type="date"
            value={expiresOn}
            onChange={(event) => setExpiresOn(event.target.value)}
            className="w-full rounded-lg border border-border-main bg-bg-main px-3 py-2 text-text-main"
          />
          <span className="block text-[10px]">Leave blank for no automatic expiry.</span>
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <PlanLimitInput label="Ask Source" value={limits.askSourcePerDay} onChange={(value) => updateLimit('askSourcePerDay', value)} />
        <PlanLimitInput label="Summaries" value={limits.sourceSummariesPerDay} onChange={(value) => updateLimit('sourceSummariesPerDay', value)} />
        <PlanLimitInput label="Create Learning" value={limits.createLearningPerDay} onChange={(value) => updateLimit('createLearningPerDay', value)} />
      </div>

      <button
        type="button"
        disabled={saving}
        onClick={() => onSave({ status, expiresOn, limits })}
        className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 px-4 py-3 text-sm font-bold text-white transition-colors"
      >
        {saving ? 'Saving pilot controls...' : 'Save Pilot Controls'}
      </button>
    </div>
  );
}

function PlanLimitInput({ label, value, onChange }: { label: string; value: number; onChange: (value: string) => void }) {
  return (
    <label className="space-y-1.5 text-sm text-text-alt">
      <span className="block font-medium">{label} per day</span>
      <input
        type="number"
        min="0"
        step="1"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-lg border border-border-main bg-bg-main px-3 py-2 text-text-main"
      />
    </label>
  );
}

function MetricCard({ title, value, icon, subtitle }: { title: string; value: any; icon: React.ReactNode; subtitle: string }) {
  return (
    <div className="bg-bg-alt border border-border-main p-6 rounded-2xl shadow-xl hover:border-text-alt/20 transition-colors w-full overflow-hidden">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-bg-main rounded-lg shrink-0">
          {icon}
        </div>
        <span className="text-sm font-medium text-text-alt truncate">{title}</span>
      </div>
      <div className="text-3xl font-bold text-text-main mb-2 truncate">{value}</div>
      <p className="text-xs text-text-alt opacity-60 truncate">{subtitle}</p>
    </div>
  );
}

function HealthItem({ label, status }: { label: string; status: string }) {
  const getStatusColor = (s: string) => {
    if (s === 'Healthy' || s === 'Optimal' || s === 'Good' || s === 'Online') return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30';
    if (s === 'High Load' || s === 'Needs Tuning') return 'text-amber-500 bg-amber-500/10 border-amber-500/30';
    return 'text-rose-500 bg-rose-500/10 border-rose-500/30';
  };

  return (
    <div className="flex items-center justify-between p-3 bg-bg-main/50 rounded-xl border border-border-main overflow-hidden">
      <span className="text-sm text-text-main opacity-80 truncate mr-2">{label}</span>
      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border whitespace-nowrap shrink-0 ${getStatusColor(status)}`}>
        {status}
      </span>
    </div>
  );
}
